// Generated by TaroBase SDK Generator: 2026-08-23T03:59:35.512Z
// Collection: siteSettings/$settingId
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * Stores public, app-wide configuration values controlled by the admin (e.g. UI display modes). Read is public so all users see the same view configuration; only ADMIN_ADDRESS can create/update/delete.
 */

export interface SiteSettingsRequest {
  value: string;
  updatedAt: number | TimeOperation | IncrementOperation | TokenAmount;
}

export interface SiteSettingsResponse {
  value: string;
  updatedAt: number;
  id: string;
  tarobase_created_at: number;
}

/**
 * Build a SiteSettings operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildSiteSettings(settingId: string, data: SiteSettingsRequest): DocumentOperation {
  const doc = transformValues(data);
  return { path: `siteSettings/${settingId}`, document: doc };
}

/**
 * Admin only. Creates a new site setting document under a known settingId (e.g. 'exploreMode'). Frontend must pass `value` (string) and `updatedAt` (Unix seconds UInt). (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setSiteSettings(settingId: string, data: SiteSettingsRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`siteSettings/${settingId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting SiteSettings: ${formatError(error)}`);
    return false;
  }
}

export type SiteSettingsRequestUpdate = Partial<SiteSettingsRequest>;

/**
 * Build a SiteSettings update operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildUpdateSiteSettings(settingId: string, data: SiteSettingsRequestUpdate): DocumentOperation {
  const doc = transformValues(data);
  return { path: `siteSettings/${settingId}`, document: doc };
}

/**
 * Admin only. Updates `value` and `updatedAt` for an existing setting. Refresh `updatedAt` to current Unix seconds when changing `value`. (Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the update worked.
 */
export async function updateSiteSettings(settingId: string, data: SiteSettingsRequestUpdate): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`siteSettings/${settingId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error updating SiteSettings: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Public. Returns the stored setting value. Anyone (including unauthenticated visitors) can read.
   (Get Single Item)
 */
export async function getSiteSettings(settingId: string): Promise<SiteSettingsResponse | null> {
  try {
    const result = await get(`siteSettings/${settingId}`);
    return result as SiteSettingsResponse | null;
  } catch (error) {
    console.error(`Error getting SiteSettings: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single SiteSettings document. (
  Read Operation Details: Public. Returns the stored setting value. Anyone (including unauthenticated visitors) can read.
  )
 */
export function subscribeSiteSettings(callback: (data: SiteSettingsResponse | null) => void, settingId: string): Promise<() => Promise<void>> {
  return subscribe(`siteSettings/${settingId}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as SiteSettingsResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to SiteSettings at path '${`siteSettings/${settingId}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many SiteSettings items from collection siteSettings
 
  Read Operation Details: Public. Returns the stored setting value. Anyone (including unauthenticated visitors) can read.
  
 */
export async function getManySiteSettings(filter: string = ""): Promise<SiteSettingsResponse[]> {
  try {
    const results = await get("siteSettings", { prompt: filter });
    return Array.isArray(results) ? results as SiteSettingsResponse[] : [];
  } catch (error) {
    console.error(`Error getting SiteSettings collection at path 'siteSettings': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in SiteSettings collection at siteSettings
 
  Read Operation Details: Public. Returns the stored setting value. Anyone (including unauthenticated visitors) can read.
  
 */
export function subscribeManySiteSettings(callback: (data: SiteSettingsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("siteSettings", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as SiteSettingsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to SiteSettings collection at path '${"siteSettings"}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all SiteSettings items from collection siteSettings
 
  Read Operation Details: Public. Returns the stored setting value. Anyone (including unauthenticated visitors) can read.
  
 */
export async function getAllSiteSettings(filter: string = ""): Promise<SiteSettingsResponse[]> {
  try {
    const results = await get("siteSettings", { prompt: filter });
    return Array.isArray(results) ? results as SiteSettingsResponse[] : [];
  } catch (error) {
    console.error(`Error getting SiteSettings collection at path 'siteSettings': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in SiteSettings collection at siteSettings
 
  Read Operation Details: Public. Returns the stored setting value. Anyone (including unauthenticated visitors) can read.
  
 */
export function subscribeAllSiteSettings(callback: (data: SiteSettingsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("siteSettings", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as SiteSettingsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to SiteSettings collection at path '${"siteSettings"}': ${formatError(error)}`);
    }
  });
}

/**
 * Count SiteSettings items in collection siteSettings.
 * Returns a numeric count instead of fetching full documents — much more efficient.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param filter - Optional natural language filter (e.g., "created in the last 7 days")
 * @returns AggregateResult with the count value
 */
export async function countSiteSettings(filter: string = ""): Promise<AggregateResult> {
  try {
    return await count("siteSettings", { prompt: filter || undefined });
  } catch (error) {
    console.error(`Error counting SiteSettings at path 'siteSettings': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * Run an aggregate operation on SiteSettings items in collection siteSettings.
 * Returns a single numeric value instead of fetching full documents.
 *
 * Supported operations: 'count', 'uniqueCount', 'sum', 'avg', 'min', 'max'.
 * For 'sum', 'avg', 'min', 'max', and 'uniqueCount', you must provide the field name.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param operation - The aggregate operation to perform
 * @param opts - Options including optional filter prompt and field name
 * @returns AggregateResult with the computed numeric value
 */
export async function aggregateSiteSettings(operation: AggregateOperation, opts?: { filter?: string; field?: string }): Promise<AggregateResult> {
  try {
    return await aggregate("siteSettings", operation, { prompt: opts?.filter || undefined, field: opts?.field });
  } catch (error) {
    console.error(`Error aggregating SiteSettings at path 'siteSettings': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * 
  Delete Operation Details: Admin only. Removes a site setting document.
   (Delete Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the delete worked.
 */
export async function deleteSiteSettings(settingId: string): Promise<boolean> {
  try {
    const succeeded = await set(`siteSettings/${settingId}`, null);
    return !!succeeded;
  } catch (error) {
    console.error(`Error deleting SiteSettings: ${formatError(error)}`);
    return false;
  }
}

/**
 * Build a delete operation for SiteSettings for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildDeleteSiteSettings(settingId: string): DocumentOperation {
  return { path: `siteSettings/${settingId}`, document: null };
}

